import Button from "@/components/UI/Button/Button";
import TextField from "@/components/UI/TextField/TextField";
import { createJob } from "@/utils/api/job-service/jobs";
import { validateEmailAddress } from "@webmonitoring/vp-common/Utils/StringValidators";
import classNames from "classnames";
import { useState } from "react";
import { getAdCampaign, getReferrer } from "@/utils/storage";
import { track } from "@/track";
import { useRouter } from "next/router";
import { publicConfig } from "@/PublicConfig";
import { toSelectOptions } from "@/utils/cms";
import Select from "@/components/UI/Select/Select";
import { createEmailCheck } from "@/utils/api/account-service/user-exists";
import { PublicPageZip } from "@/utils/page";
import { JobGetResponse } from "@/types/job";
import Section from "@/components/Section/Section";
import SectionContainer from "@/components/Section/SectionContainer";
import SectionHeader from "@/components/Section/SectionHeader";

interface Section18Props extends React.HTMLAttributes<HTMLElement> {
  job?: JobGetResponse;
  page: any;
  section: any;
}

const EmailAlertSection = ({ className, job, page, section, ...props }: Section18Props) => {
  const [createAccountLoading, setCreateAccountLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [interval, setInterval] = useState(section?.IntervalSelect?.OptionDefaultValue ?? "1440");

  const router = useRouter();

  return (
    <Section className={classNames("bg-vpBlue-200", className)} section={section} {...props}>
      <SectionContainer className="lg:max-w-screen-lg" section={section}>
        <SectionHeader section={section} />
        <form
          className="flex flex-col gap-5"
          onSubmit={async (event) => {
            event.preventDefault();

            const validateEmailAddressResult = validateEmailAddress(email);

            if (validateEmailAddressResult === "malformed") {
              setEmailError(section?.EmailDefaultErrorMessage);
              return;
            }

            setCreateAccountLoading(true);

            try {
              const response = await createEmailCheck({
                payload: {
                  email,
                },
              });

              if (response.status !== "no") {
                if (response.status === "active") {
                  setEmailError(section?.CreateAccountEmailExistsErrorMessage);
                } else {
                  setEmailError(section?.CreateAccountDefaultErrorMessage);
                }

                setCreateAccountLoading(false);

                return;
              }
            } catch (error) {
              setCreateAccountLoading(false);
              setEmailError(section?.CreateAccountDefaultErrorMessage);
              return;
            }

            setEmailError("");

            try {
              await createJob({
                payload: {
                  active: true,
                  ad_campaign: getAdCampaign(),
                  crop: job?.crop,
                  description: job?.description,
                  email,
                  interval,
                  keyword_action: job?.keyword_action,
                  keywords: job?.keywords,
                  locale: page?.attributes?.locale,
                  mode: job?.mode,
                  notification: {
                    configuration: {},
                    enableEmailAlert: true,
                    enableSmsAlert: false,
                  },
                  notification_threshold: job?.notification_threshold,
                  proxy_id: job?.proxy_id,
                  siteReferer: getReferrer(),
                  target_device: "4",
                  trigger: job?.notification_threshold ? String(job?.notification_threshold) : undefined,
                  url: job?.url,
                  wait_time: job?.wait_time,
                  xpath: job?.xpath,
                },
              });
            } catch (_error) {
              setEmailError(section?.CreateAccountDefaultErrorMessage);
              return;
            }

            track.signUp.signUpStart({
              actions: [],
              compare_type: job?.mode,
              frequency: String(interval),
              URL: job?.url,
            });

            const mainAppRootUrl = new URL(publicConfig.visualpingWebURL);

            mainAppRootUrl.pathname =
              page?.attributes?.locale === "en" ? "/sign-up" : `${page?.attributes?.locale}/sign-up'`;

            mainAppRootUrl.searchParams.set("email", email);

            window.location.href = mainAppRootUrl.toString();
          }}
        >
          <div className="flex flex-col items-center justify-end gap-4 lg:flex-row">
            {section?.EmailTextField && (
              <div className="flex w-full flex-1 flex-col gap-2">
                <label htmlFor="section18-email" className="text-neutral-700">
                  {section?.EmailTextField?.Label}
                </label>
                <TextField
                  error={Boolean(emailError)}
                  errorMessage={emailError}
                  errorTooltipProps={{
                    color: "error",
                    show: true,
                  }}
                  id={`${section.id}-email`}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  name="email"
                  placeholder={section?.EmailTextField?.Placeholder}
                  size="large"
                  value={email}
                />
              </div>
            )}
            {section?.IntervalSelect && (
              <div className="flex w-full flex-col gap-2 lg:max-w-[200px]">
                <label htmlFor="section18-frequency" className="text-neutral-700">
                  {section?.IntervalSelect?.Label}
                </label>
                <Select
                  name="frequency-select"
                  id={`${section.id}-frequency-select`}
                  onChange={(nextValue: string) => {
                    setInterval(nextValue);
                  }}
                  options={toSelectOptions(section.IntervalSelect)?.map((option: any) => {
                    return {
                      children: option?.label,
                      value: option?.value ?? option?.label,
                    };
                  })}
                  size="large"
                  value={interval}
                />
              </div>
            )}
            {section?.SubmitButton && (
              <Button className="w-full lg:max-w-[200px] lg:self-end" loading={createAccountLoading}>
                {section?.SubmitButton?.Value}
              </Button>
            )}
          </div>
        </form>
      </SectionContainer>
    </Section>
  );
};

export default EmailAlertSection;
